import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./Supplier.css";
import axiosInstance from "../../../../Config/axiosInstance";

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddContactPopup from "../ContactPersons/AddContacts";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";

const AddSupplier = () => {
  const [isContactPopupOpen, setContactPopupOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [createdSupplierId, setCreatedSupplierId] = useState(null);
  const [existingSupplierInfo, setExistingSupplierInfo] = useState(null);
  const [isDepartmentSelectPopupOpen, setDepartmentSelectPopupOpen] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [showCategory, setShowCategory] = useState(false);
  const animatedComponents = makeAnimated();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    fiscal_code: "",
    phone: "",
    email: "",
    address: "",
    departments: [],
    contacts: [],
    nr_crt: null,
    category: [],
    status: "Active",
  });

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  useEffect(() => {
    axiosInstance.get("/category/")
      .then((response) => {
        const options = response.data.map((cat) => ({
          value: cat.id,
          label: cat.title,
        }));
        setCategoryOptions(options);
      })
      .catch((error) => console.error("Error fetching category:", error));
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get("/department/user/");
        setDepartments(response.data);
      } catch (error) {
        console.error("Eroare la preluarea departamentelor", error);
      }
    };

    fetchDepartments();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'fiscal_code' && value.length > 4) {
      checkFiscalCode(value);
    }
  };

  const handleDepartmentChange = (selectedOptions) => {
    const selectedIds = selectedOptions.map((opt) => opt.value);
    setFormData({ ...formData, departments: selectedIds });
  };

  const handleCategoryChange = (selectedOptions) => {
    setFormData({ ...formData, category: selectedOptions.map((opt) => opt.value) });
  };

  const handleAddContact = () => setContactPopupOpen(true);
  const handleCloseContactPopup = () => setContactPopupOpen(false);

  const handleAddContactToList = (newContact) => {
    setFormData((prevData) => ({
      ...prevData,
      contacts: [...prevData.contacts, newContact],
    }));
  };

  const checkFiscalCode = async (fiscalCode) => {
    try {
      const response = await axiosInstance.get(`/check_fiscal_code/?fiscal_code=${fiscalCode}`);
      if (response.data.exists) {
        setExistingSupplierInfo(response.data);
      } else {
        setExistingSupplierInfo(null);
      }
    } catch (error) {
      console.error("Error checking fiscal code:", error);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (formData.fiscal_code && formData.fiscal_code.length > 4) {
        checkFiscalCode(formData.fiscal_code);
      }
    }, 100);
    return () => clearTimeout(delayDebounce);
  }, [formData.fiscal_code]);

  const isProcurementSelected = () => {
    return departments.some(
      (dept) =>
        dept.label.toLowerCase() === "procurement" &&
        formData.departments.includes(dept.value)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError("**Please enter a valid email address.");
      hasError = true;
    } else {
      setEmailError("");
    }

    const phoneRegex = /^\+?[0-9]\d{1,19}$/;
    if (!phoneRegex.test(formData.phone)) {
      setPhoneError("**Please enter a valid phone number.");
      hasError = true;
    } else {
      setPhoneError("");
    }

    if (hasError) {
      return;
    }

    const procurementIsSelected = isProcurementSelected();
    const categoryIsEmpty = !formData.category || formData.category.length === 0;

    if (procurementIsSelected && categoryIsEmpty) {
      alert("Category is required when the Procurement department is selected.");
      return;
    }

    try {
      const { contacts, category, status, departments, ...rest } = formData;

      const departmentStatuses = departments.map((deptId) => ({
        department: deptId,
        status: status,
      }));

      const supplierData = {
        ...rest,
        departments,
        category,
        department_statuses: departmentStatuses,
      };

      const response = await axiosInstance.post("/suppliers/", supplierData);
      const newSupplierId = response.data.id;
      setCreatedSupplierId(newSupplierId);

      if (contacts.length > 0) {
        const contactRequests = contacts.map((contact) =>
          axiosInstance.post("/supplier-contact/", { ...contact, supplier: newSupplierId })
        );
        await Promise.all(contactRequests);
      }

      navigate("/supplier");
    } catch (error) {
      console.error("Error adding supplier:", error.response?.data || error.message);
    }
  };


  return (
    <>
      <div className='supplier'>
        <div className="add-supplier-container">
          <div className="supplier-header">
            <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
            <h2 className="add-supplier-title">Add Supplier</h2>
          </div>
        </div>

        <hr className="title-separator" />

        <div className="add-supplier-container">
          <form onSubmit={handleSubmit} className="add-supplier-form">
            <div className="form-group">
              <label htmlFor="name" className="label">
                Name<span className="required-star">*</span>
              </label>
              <input id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input" />
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label htmlFor="fiscal_code" className="label">
                  CUI<span className="required-star">*</span>
                </label>
                <input
                  id="fiscal_code"
                  name="fiscal_code"
                  value={formData.fiscal_code}
                  onChange={handleChange}
                  required
                  className="input"
                />
                {phoneError && (
                  <span className="supplier-error"> &nbsp;</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="label">
                  Phone<span className="required-star">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input"
                  required
                />
                {phoneError && (
                  <span className="supplier-error"> {phoneError}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="label">
                Email<span className="required-star">*</span>
              </label>
              <input
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                required
              />
              {emailError && <span className="supplier-error">{emailError}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="address" className="label">
                Address<span className="required-star">*</span>
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="textarea"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="departments" className="label">
                Departments<span className="required-star">*</span>
              </label>
              <Select
                id="departments"
                name="departments"
                options={departments}
                value={departments.filter((d) => formData.departments.includes(d.value))}
                onChange={handleDepartmentChange}
                isMulti
                components={animatedComponents}
                className="multiselect-field"
                required
              />
            </div>

            {isProcurementSelected() && (
              <div className="form-group">
                <label htmlFor="category" className="label">
                  Category<span className="required-star">*</span>
                </label>
                <Select
                  id="category"
                  name="category"
                  options={categoryOptions}
                  value={categoryOptions.filter((cat) => formData.category.includes(cat.value))}
                  onChange={handleCategoryChange}
                  isMulti
                  components={animatedComponents}
                  className="multiselect-field"
                />
              </div>
            )}

            <div className="form-group-row">
              <div className="form-group select-container">
                <label htmlFor="contacts" className="label">Contact Persons</label>
                <Select
                  id="contacts"
                  name="contacts"
                  options={formData.contacts.map(contact => ({
                    value: contact.email,
                    label: `${contact.name}`,
                  }))}
                  value={formData.contacts.map(contact => ({
                    value: contact.email,
                    label: `${contact.name}`,
                  }))}
                  isMulti
                  isDisabled
                  className="multiselect-field"
                />
              </div>

              <button type="button" className="button add-contact" onClick={handleAddContact}>
                Add Contact
              </button>
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="label">
                  Status<span className="required-star">*</span>
                </label>
                <Select
                  name="status"
                  options={statusOptions}
                  value={statusOptions.find(opt => opt.value === formData.status)}
                  onChange={(selectedOption) =>
                    setFormData({ ...formData, status: selectedOption.value })
                  }
                  className="multiselect-field"
                  components={animatedComponents}
                  isClearable={false}
                  isSearchable={false}
                  required
                />
              </div>

              <div className="button-group-inline">
                <button type="button" className="button discard" onClick={() => navigate(-1)}>
                  Discard
                </button>
                <button type="submit" className="button save">Save</button>
              </div>
            </div>
          </form>

          <AddContactPopup
            isOpen={isContactPopupOpen}
            onClose={handleCloseContactPopup}
            onAddContact={handleAddContactToList}
            supplierId={createdSupplierId}
          />

          {existingSupplierInfo && (
            <Dialog
              open={Boolean(existingSupplierInfo)}
              onClose={() => setExistingSupplierInfo(null)}
            >
              <DialogTitle sx={{ px: 3 }}>
                A supplier with this CUI already exists!
              </DialogTitle>
              <DialogContent sx={{ px: 3 }}>
                <DialogContentText>
                  The supplier <strong>"{existingSupplierInfo?.name}"</strong> with CUI <strong>"{existingSupplierInfo?.fiscal_code}"</strong> already exists in the system.
                  It is currently associated with the following departments:
                  <strong>
                    {existingSupplierInfo?.departments.map(dep => dep.name).join(", ")}
                  </strong>.
                  <br /><br />
                  Would you like to associate this supplier with additional departments?
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  onClick={() => {
                    setDepartmentSelectPopupOpen(true);
                  }}
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                >
                  Yes
                </Button>
                <Button
                  onClick={() => setExistingSupplierInfo(null)}
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                >
                  No
                </Button>
              </DialogActions>
            </Dialog>
          )}

          <Dialog
            open={isDepartmentSelectPopupOpen}
            onClose={() => setDepartmentSelectPopupOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Select Additional Departments</DialogTitle>
            <DialogContent dividers style={{ minHeight: "400px", overflowY: "auto" }}>
              <DialogContentText>
                Select the departments to associate with the supplier <strong>{existingSupplierInfo?.name}</strong> (CUI: <strong>{existingSupplierInfo?.fiscal_code}</strong>):
              </DialogContentText>

              <div style={{ marginTop: '1rem' }}>
                <Select
                  isMulti
                  options={departments}
                  components={animatedComponents}
                  onChange={(selectedOptions) => {
                    setSelectedDepartments(selectedOptions);
                    const includesProcurement = selectedOptions.some(opt => opt.label.toLowerCase() === 'procurement');
                    setShowCategory(includesProcurement);
                  }}
                />
              </div>

              {showCategory && (
                <div style={{ marginTop: '1rem' }}>
                  <label>Category<span className="required-star">*</span></label>
                  <Select
                    isMulti
                    options={categoryOptions}
                    components={animatedComponents}
                    onChange={(selected) => setSelectedCategories(selected)}
                  />
                </div>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    if (!existingSupplierInfo?.supplier_id) {
                      console.error("Eroare: ID-ul furnizorului nu a fost găsit.");
                      return;
                    }

                    const existingDepartmentIds = (existingSupplierInfo.departments || [])
                      .map(dep => dep.id);

                    const selectedDepartmentIds = selectedDepartments.map(opt => Number(opt.value));

                    const newDepartmentIds = selectedDepartmentIds.filter(id => !existingDepartmentIds.includes(id));

                    const departmentStatuses = newDepartmentIds.map(deptId => ({
                      department: deptId,
                      status: "Active",
                    }));

                    const mergedDepartmentIds = [...new Set([...existingDepartmentIds, ...selectedDepartmentIds])];
                    const fullSupplierResp = await axiosInstance.get(`/suppliers/${existingSupplierInfo.supplier_id}/`);

                    const existingCategoryIds = (fullSupplierResp.data.category || []).map(cat =>
                      typeof cat === 'object' ? Number(cat.id) : Number(cat)
                    );

                    const selectedCategoryIds = selectedCategories.map(cat => Number(cat.value));
                    const combinedCategoryIds = [...new Set([...existingCategoryIds, ...selectedCategoryIds])];
                    const patchUrl = `/suppliers/${existingSupplierInfo.supplier_id}/`;

                    await axiosInstance.patch(patchUrl, {
                      departments: mergedDepartmentIds,
                      category: combinedCategoryIds,
                    });

                    if (departmentStatuses.length > 0) {
                      const statusRequests = departmentStatuses.map((entry) =>
                        axiosInstance.post("/status-supplier-department/", {
                          supplier: existingSupplierInfo.supplier_id,
                          department: entry.department,
                          status: entry.status,
                        })
                      );
                      await Promise.all(statusRequests);
                    }

                    setDepartmentSelectPopupOpen(false);
                    navigate("/supplier");
                  } catch (error) {
                    console.error("Error updating supplier:", error.response?.data || error.message);
                    alert("An error occurred while updating the supplier.");
                  }
                }}
              >
                Save
              </Button>

              <Button
                onClick={() => {
                  setDepartmentSelectPopupOpen(false);
                  setExistingSupplierInfo(null);
                  setShowCategory(false);
                  setSelectedCategories([]);
                  setSelectedDepartments([]);
                }}
                color="inherit"
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default AddSupplier;